/**
 * "All Projects" — one board across every project the viewer can see (V6-03).
 *
 * The design problem and its answer, restated because the code only makes
 * sense with it: different projects have different status sets, so their
 * columns do not line up. The shared language is the **status category** every
 * project status already maps to. Columns are the categories; each card wears
 * its project badge and its own project's status as a small label.
 *
 * There is no manual rank here. A rank across projects means nothing, and the
 * per-project rank on each project's own board is left exactly as it was.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  useDroppable,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { KanbanSquare, Link2Off, Loader2, Lock, MessageSquare, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  PRIORITIES,
  PRIORITY_COLOURS,
  type AllProjectsBoardCard,
  type AllProjectsBoardFilters,
  type AllProjectsBoardProject,
  type Priority,
  type StatusCategory,
} from '@virtuetasks/types';
import { PageHeader } from '@/app/layout/AppShell';
import { Button } from '@/components/ui/button';
import { FilterPill } from './FilterPill';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { DueChip } from '@/components/shared/TaskRow';
import { UserLink } from '@/components/shared/UserLink';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useUsers } from '@/features/settings/api';
import { subscribeToRoom } from '@/lib/realtime';
import { CANNOT_MOVE, useBoardSensors, useCanMoveTask } from './boardDnd';
import { useLabels, type TaskView } from './api';
import {
  useAllProjectsBoard,
  useBoardFilters,
  useMoveOnAllProjectsBoard,
  useSaveBoardFilters,
  type ColumnCursors,
} from './allProjectsApi';

type People = Record<string, { name: string; avatarUrl?: string }>;

/** Where a drop lands when the target project has more than one candidate. */
interface StatusChoice {
  taskId: string;
  taskKey: string;
  category: StatusCategory;
  options: { id: string; name: string }[];
}

function Card({
  task,
  project,
  people,
  locked,
  dragging,
  onOpen,
}: {
  task: AllProjectsBoardCard;
  project?: AllProjectsBoardProject;
  people: People;
  locked?: boolean;
  dragging?: boolean;
  onOpen?: () => void;
}) {
  return (
    <article
      className={cn(
        'vt-surface group relative select-none py-2 pl-3 pr-2',
        locked ? 'cursor-default' : 'cursor-grab active:cursor-grabbing',
        dragging && 'opacity-40',
      )}
      data-testid="all-board-card"
      data-task-key={task.key}
      data-project-key={task.projectKey}
      onDoubleClick={onOpen}
    >
      <span className="vt-spine" style={{ background: PRIORITY_COLOURS[task.priority] }} aria-hidden />

      <div className="flex items-center gap-1.5">
        {/* The project badge is what makes a mixed column readable at all. */}
        <span
          className="vt-mono shrink-0 rounded-[4px] px-1 py-0.5 text-[10px] font-medium"
          data-testid="card-project-badge"
          style={{
            background: 'var(--surface-2)',
            color: project?.colour || 'var(--text-muted)',
          }}
          title={project?.name ?? task.projectKey}
        >
          {project?.icon ? `${project.icon} ` : ''}
          {task.projectKey}
        </span>
        <button
          type="button"
          onClick={onOpen}
          className="vt-mono shrink-0 text-[10px] vt-muted hover:text-[var(--accent)]"
        >
          {task.key}
        </button>
        {task.blockedBy.length > 0 && (
          <span title={task.blockedReason ?? 'Blocked'} className="text-[var(--danger)]">
            <Link2Off className="size-3" />
          </span>
        )}
        {locked && (
          <span
            className="ml-auto opacity-0 transition-opacity group-hover:opacity-60"
            title={CANNOT_MOVE}
            data-testid="card-lock"
          >
            <Lock className="size-3" />
          </span>
        )}
      </div>

      <p className="mt-1 line-clamp-3 text-[13px] leading-snug">{task.title}</p>

      <div className="mt-2 flex items-center gap-2">
        {/* The project's own status, because "In progress" is the column and
            "In QA" is the fact. */}
        <span className="truncate text-[10px] vt-muted" data-testid="card-status-name">
          {task.statusName}
        </span>
        <DueChip dueDate={task.dueDate} done={task.statusCategory === 'done'} />
        {task.commentCount > 0 && (
          <span className="flex items-center gap-0.5 text-[10px] vt-muted">
            <MessageSquare className="size-2.5" />
            {task.commentCount}
          </span>
        )}
        <span className="ml-auto flex -space-x-1.5">
          {task.assigneeIds.slice(0, 3).map((id) => (
            <UserLink
              key={id}
              userId={id}
              name={people[id]?.name ?? 'Unknown'}
              avatarUrl={people[id]?.avatarUrl}
              avatarOnly
              size={18}
              className="ring-1 ring-[var(--surface)]"
            />
          ))}
        </span>
      </div>
    </article>
  );
}

function SortableCard(props: Parameters<typeof Card>[0] & { canMove: boolean }) {
  const { canMove, ...cardProps } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.task._id,
    disabled: !canMove,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      data-testid={canMove ? undefined : 'locked-card'}
      title={canMove ? undefined : CANNOT_MOVE}
      onPointerDownCapture={
        canMove ? undefined : () => toast.error(CANNOT_MOVE, { id: 'all-board-locked' })
      }
      {...attributes}
      {...listeners}
    >
      <Card {...cardProps} dragging={isDragging} locked={!canMove} />
    </div>
  );
}

function Column({
  category,
  label,
  count,
  note,
  tasks,
  projectsById,
  people,
  hasMore,
  onShowMore,
  onOpen,
}: {
  category: StatusCategory;
  label: string;
  count: number;
  note?: string;
  tasks: AllProjectsBoardCard[];
  projectsById: Map<string, AllProjectsBoardProject>;
  people: People;
  hasMore: boolean;
  onShowMore: () => void;
  onOpen: (task: AllProjectsBoardCard) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: category });
  const canMove = useCanMoveTask();

  return (
    <section
      ref={setNodeRef}
      className={cn(
        'flex w-[288px] shrink-0 flex-col rounded-[6px] bg-[var(--surface-2)]',
        isOver && 'outline outline-2 outline-[var(--accent)]',
      )}
      data-testid="all-board-column"
      data-category={category}
      data-over={isOver ? 'true' : undefined}
    >
      <header className="flex items-center gap-1.5 px-2 py-1.5">
        <h2 className="truncate text-[12px] font-medium">{label}</h2>
        <span className="vt-mono ml-auto text-[11px]" data-testid="column-count">
          {count}
        </span>
      </header>
      {note && <p className="px-2 pb-1 text-[10px] vt-muted">{note}</p>}

      <SortableContext items={tasks.map((t) => t._id)} strategy={verticalListSortingStrategy}>
        <div className="flex min-h-[60px] flex-col gap-1.5 overflow-y-auto px-1.5 pb-2">
          {tasks.map((task) => (
            <SortableCard
              key={task._id}
              task={task}
              project={projectsById.get(task.projectId)}
              people={people}
              canMove={canMove(task as unknown as TaskView)}
              onOpen={() => onOpen(task)}
            />
          ))}
          {tasks.length === 0 && (
            <p className="px-2 py-4 text-center text-[11px] vt-muted">Nothing here</p>
          )}
          {hasMore && (
            <Button
              variant="ghost"
              size="sm"
              data-testid="column-show-more"
              onClick={onShowMore}
            >
              Show more
            </Button>
          )}
        </div>
      </SortableContext>
    </section>
  );
}

/**
 * "In review → QA or Client review?" — §3.
 *
 * Only ever shown when the target project genuinely has more than one status in
 * the dropped category. Guessing would silently put work in the wrong column of
 * somebody else's project.
 */
function StatusPopover({
  choice,
  onPick,
  onCancel,
}: {
  choice: StatusChoice;
  onPick: (statusId: string) => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onCancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/20 p-4"
      data-testid="status-choice"
      role="dialog"
      aria-modal="true"
      aria-label={`Which status for ${choice.taskKey}?`}
      onClick={onCancel}
    >
      <div
        className="vt-surface vt-elevated flex w-[280px] flex-col gap-1 p-2"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-1 pb-1">
          <span className="flex-1 text-[12px] font-medium">
            {choice.taskKey} → which status?
          </span>
          <button type="button" aria-label="Cancel" onClick={onCancel}>
            <X className="size-3.5 vt-muted" />
          </button>
        </div>
        {choice.options.map((option, index) => (
          <button
            key={option.id}
            type="button"
            autoFocus={index === 0}
            data-testid="status-choice-option"
            data-status-id={option.id}
            onClick={() => onPick(option.id)}
            className="rounded-[6px] px-2 py-1.5 text-left text-[13px] hover:bg-[var(--surface-2)] focus:bg-[var(--surface-2)] focus:outline-none"
          >
            {option.name}
          </button>
        ))}
      </div>
    </div>
  );
}

function Filters({
  filters,
  projects,
  labels,
  people,
  onChange,
}: {
  filters: AllProjectsBoardFilters;
  projects: AllProjectsBoardProject[];
  labels: { _id: string; name: string }[];
  people: { _id: string; name: string }[];
  onChange: (next: AllProjectsBoardFilters) => void;
}) {
  function set<K extends keyof AllProjectsBoardFilters>(
    key: K,
    value: AllProjectsBoardFilters[K],
  ): void {
    const next = { ...filters, [key]: value };
    if (value === undefined || (Array.isArray(value) && value.length === 0)) delete next[key];
    onChange(next);
  }

  const active =
    Object.keys(filters).filter((key) => {
      const value = filters[key as keyof AllProjectsBoardFilters];
      return Array.isArray(value) ? value.length > 0 : Boolean(value);
    }).length > 0;

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="all-board-filters">
      {/*
        V7-03 §1, §2 — one pill component for every filter.

        These were six bare `<select>`s, and the project one was a `multiple`
        select with `size={1}`: a squat scrolling box showing one row at a time,
        unsearchable, and silent about how many things were picked. That is the
        control in the founder's screenshot. §2 says to delete the bespoke
        version rather than style it into agreement, so the whole row is now the
        same pill — otherwise fixing the odd one out would simply have made the
        other five odd instead.
      */}
      <FilterPill
        label="Projects"
        emptyLabel="All projects"
        noun="project"
        testId="filter-projects"
        options={projects.map((project) => ({
          value: project._id,
          label: project.name,
          badge: project.key,
          colour: project.colour,
        }))}
        selected={filters.projectId ?? []}
        onChange={(next) => set('projectId', next.length ? next : undefined)}
      />

      <FilterPill
        label="Assignee"
        emptyLabel="Anyone"
        noun="person"
        nounPlural="people"
        multiple={false}
        testId="filter-assignee"
        options={[
          { value: 'unassigned', label: 'Unassigned' },
          ...people.map((person) => ({ value: person._id, label: person.name })),
        ]}
        selected={filters.assigneeId ?? []}
        onChange={(next) => set('assigneeId', next.length ? next : undefined)}
      />

      <FilterPill
        label="Priority"
        emptyLabel="Any priority"
        noun="priority"
        nounPlural="priorities"
        multiple={false}
        searchable={false}
        testId="filter-priority"
        options={PRIORITIES.map((priority) => ({ value: priority, label: priority }))}
        selected={filters.priority ?? []}
        onChange={(next) => set('priority', next.length ? (next as Priority[]) : undefined)}
      />

      <FilterPill
        label="Label"
        emptyLabel="Any label"
        noun="label"
        multiple={false}
        testId="filter-label"
        options={labels.map((label) => ({ value: label._id, label: label.name }))}
        selected={filters.labelId ?? []}
        onChange={(next) => set('labelId', next.length ? next : undefined)}
      />

      <FilterPill
        label="Due"
        emptyLabel="Any date"
        noun="date"
        multiple={false}
        searchable={false}
        testId="filter-due"
        options={[
          { value: 'overdue', label: 'Overdue' },
          { value: 'this_week', label: 'This week' },
        ]}
        selected={filters.dueBucket ? [filters.dueBucket] : []}
        onChange={(next) =>
          set('dueBucket', (next[0] as AllProjectsBoardFilters['dueBucket']) ?? undefined)
        }
      />

      <Button
        variant={filters.justMine ? 'subtle' : 'ghost'}
        size="sm"
        data-testid="filter-just-mine"
        aria-pressed={Boolean(filters.justMine)}
        onClick={() => set('justMine', filters.justMine ? undefined : true)}
      >
        Just mine
      </Button>

      {active && (
        <Button variant="ghost" size="sm" data-testid="filter-clear" onClick={() => onChange({})}>
          Clear
        </Button>
      )}
    </div>
  );
}

export function AllProjectsBoard() {
  const navigate = useNavigate();
  const { data: savedFilters, isLoading: filtersLoading } = useBoardFilters();
  const saveFilters = useSaveBoardFilters();
  const { data: people = [] } = useUsers();
  const { data: labels = [] } = useLabels();
  const move = useMoveOnAllProjectsBoard();

  const [filters, setFilters] = useState<AllProjectsBoardFilters>({});
  const [cursors, setCursors] = useState<ColumnCursors>({});
  const [activeId, setActiveId] = useState<string | null>(null);
  const [choice, setChoice] = useState<StatusChoice | null>(null);
  const [hydrated, setHydrated] = useState(false);

  // The persisted set is the starting state, once. After that the screen owns
  // it, or every save would bounce back through this effect.
  useEffect(() => {
    if (hydrated || filtersLoading) return;
    setFilters(savedFilters ?? {});
    setHydrated(true);
  }, [hydrated, filtersLoading, savedFilters]);

  const { data, isLoading } = useAllProjectsBoard(filters, cursors);

  const projectsById = useMemo(
    () => new Map((data?.projects ?? []).map((project) => [project._id, project])),
    [data],
  );
  const peopleById = useMemo(
    () => Object.fromEntries(people.map((p) => [p._id, { name: p.name, avatarUrl: p.avatarUrl }])),
    [people],
  ) as People;

  const allTasks = useMemo(
    () => (data?.columns ?? []).flatMap((column) => column.tasks),
    [data],
  );

  const columnIds = useMemo(
    () => (data?.columns ?? []).map((column) => column.category as string),
    [data],
  );
  const sensors = useBoardSensors(columnIds);

  // Task changes are emitted to `project:<id>`, so a viewer has to ask for each
  // project they are looking at — which here is all of them.
  useProjectRooms(data?.projects ?? []);

  function applyFilters(next: AllProjectsBoardFilters): void {
    setFilters(next);
    // Cursors describe a page of the *previous* filter set; keeping them would
    // page into a result that no longer exists.
    setCursors({});
    saveFilters.mutate(next);
  }

  function showMore(category: StatusCategory, cursor: string | null): void {
    if (!cursor) return;
    setCursors((previous) => ({ ...previous, [category]: cursor }));
  }

  async function moveTo(taskId: string, statusId: string): Promise<void> {
    try {
      await move.mutateAsync({ id: taskId, statusId });
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message || CANNOT_MOVE : 'Could not move the card',
      );
    }
  }

  function onDragEnd(event: DragEndEvent): void {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const task = allTasks.find((candidate) => candidate._id === String(active.id));
    if (!task) return;

    const overTask = allTasks.find((candidate) => candidate._id === String(over.id));
    const category = (overTask?.statusCategory ?? String(over.id)) as StatusCategory;

    // §3: a drop inside the same column does nothing. There is no manual rank
    // here, so the card animates back rather than pretending to reorder.
    if (category === task.statusCategory) return;

    const project = projectsById.get(task.projectId);
    const options = (project?.statuses ?? []).filter((status) => status.category === category);

    if (options.length === 0) {
      toast.error(`${project?.name ?? 'That project'} has no ${category.replace('_', ' ')} status`);
      return;
    }
    if (options.length === 1) {
      void moveTo(task._id, options[0].id);
      return;
    }

    setChoice({
      taskId: task._id,
      taskKey: task.key,
      category,
      options: options.map((status) => ({ id: status.id, name: status.name })),
    });
  }

  const activeTask = activeId ? allTasks.find((task) => task._id === activeId) : null;
  const visibleProjects = data?.projects ?? [];

  return (
    <>
      <PageHeader
        title="All Projects"
        subtitle={
          visibleProjects.length === 1
            ? '1 project'
            : `${visibleProjects.length} projects, by status category`
        }
        toolbar={
          <Filters
            filters={filters}
            projects={visibleProjects}
            labels={labels}
            people={people}
            onChange={applyFilters}
          />
        }
      />

      <div className="p-4">
        {isLoading && !data ? (
          <div className="flex gap-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-[50vh] w-[288px]" />
            ))}
          </div>
        ) : visibleProjects.length === 0 ? (
          <EmptyState
            icon={<KanbanSquare className="size-6" />}
            title="No projects to show"
            description="You are not on any project yet, so there is nothing to put on this board."
          />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={(event: DragStartEvent) => setActiveId(String(event.active.id))}
            onDragEnd={onDragEnd}
          >
            <div className="flex gap-3 overflow-x-auto pb-2">
              {(data?.columns ?? []).map((column) => (
                <Column
                  key={column.category}
                  category={column.category}
                  label={column.label}
                  count={column.count}
                  note={
                    column.category === 'done' || column.category === 'cancelled'
                      ? `Last ${data?.doneWindowDays ?? 14} days`
                      : undefined
                  }
                  tasks={column.tasks}
                  projectsById={projectsById}
                  people={peopleById}
                  hasMore={Boolean(column.nextCursor)}
                  onShowMore={() => showMore(column.category, column.nextCursor)}
                  onOpen={(task) => navigate(`/tasks/${task.key}`)}
                />
              ))}
            </div>

            <DragOverlay>
              {activeTask ? (
                <Card
                  task={activeTask}
                  project={projectsById.get(activeTask.projectId)}
                  people={peopleById}
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        )}

        {move.isPending && (
          <p className="mt-2 flex items-center gap-1.5 text-[12px] vt-muted">
            <Loader2 className="size-3 animate-spin" />
            Moving…
          </p>
        )}
      </div>

      {choice && (
        <StatusPopover
          choice={choice}
          onCancel={() => setChoice(null)}
          onPick={(statusId) => {
            const { taskId } = choice;
            setChoice(null);
            void moveTo(taskId, statusId);
          }}
        />
      )}
    </>
  );
}

/**
 * One room per visible project, so another person's drag lands live.
 *
 * Task events go to `project:<id>` rather than workspace-wide, which is right —
 * but it means this board has to ask for every project it is showing. Bounded
 * by how many projects a person can see, which is the same number the sidebar
 * already lists.
 */
function useProjectRooms(projects: AllProjectsBoardProject[]): void {
  const ids = projects.map((project) => project._id).join(',');
  useEffect(() => {
    if (!ids) return;
    const leavers = ids.split(',').map((id) => subscribeToRoom(`project:${id}`));
    return () => {
      for (const leave of leavers) leave();
    };
  }, [ids]);
}
